import { CommonModule } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { flatten } from "lodash";
import { firstValueFrom } from "rxjs";
import { DropdownCellEditor } from "src/app/common/components/core/table/cell-editors/dropdown-cell-editor.component";
import { ClientSideListBaseComponent } from "src/app/common/core/client-side-list-base/client-side-list-base.component";
import { DropdownComponent } from "../../../common/components/core/dropdown/dropdown.component";
import {
  HierarchyDataItem,
  HierarchyLevel,
} from "../../../common/components/hierarchy/hierarchy.interface";
import { LevelMode } from "../../../common/enums/level-mode.enum";
import { Level } from "../../../common/interfaces/level.interface";
import { FacilitiesClient } from "../../../common/services/facility.client";
import { LevelClient } from "../../../common/services/level.client";
import { ICellRendererParams } from "ag-grid-community";
@Component({
  selector: "thera-level-modal",
  templateUrl: "./level-modal.component.html",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    DropdownComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    ClientSideListBaseComponent,
  ],
})
export class LevelModalComponent implements OnInit {
  mode!: LevelMode;
  currentLevel!: HierarchyLevel;
  parentLevel!: HierarchyLevel;
  form!: FormGroup;
  levelMode = LevelMode;
  corpId: any;
  colDefs = [
    {
      headerName: "Facility Name",
      field: "facilityname",
      cellRenderer: (params: ICellRendererParams) => {
        if (params.value) {
          return params.value;
        }

        return "—";
      },
    },
    {
      headerName: "Reassign",
      field: "",
      cellRenderer: DropdownCellEditor,
      cellRendererParams: {
        options: [
          ...(this.data.reassignableLevels || []),
          { id: null, name: "Select" },
        ],
        onChange: this.onFacilityReassignmentChange.bind(this),
      },
    },
  ];
  reassignmentMap: any = {};

  constructor(
    public dialogRef: MatDialogRef<LevelModalComponent>,
    private formGroupBuilder: FormBuilder,
    private levelClient: LevelClient,
    private facilityClient: FacilitiesClient,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.mode = data.mode;
    this.currentLevel = data.currentLevel;
    this.parentLevel = data.parentLevel;
    this.corpId = data.corpId;
  }

  ngOnInit(): void {
    this.dialogRef.addPanelClass("custom-dialog-container");
    this.form = this.formGroupBuilder.group({
      name: ["", Validators.required],
    });

    if (this.mode === LevelMode.EDIT) {
      this.form.get("name")?.setValue(this.currentLevel.name);
    }
  }

  async onSave() {
    this.form.markAllAsTouched();
    if (this.form.valid || this.mode === LevelMode.DELETE) {
      try {
        if (this.mode === LevelMode.ADD) {
          const level: Level = {
            levelname: this.form.get("name")?.value,
            parentlevelid: this.parentLevel?.id,
          } as Level;
          await firstValueFrom(this.levelClient.addLevel(level, this.corpId));
        } else if (this.mode === LevelMode.EDIT) {
          const level: Level = {
            levelname: this.form.get("name")?.value,
            id: this.currentLevel.id,
          } as Level;
          await firstValueFrom(this.levelClient.editLevel(level, this.corpId));
        } else {
          const unassignedFacilities = this.data.facilitiesToReAssign.filter(
            (facility: HierarchyDataItem) =>
              !Object.values(this.reassignmentMap).some(
                (x: any) => x.id === facility.id
              )
          );

          const facilityUnAssignments = unassignedFacilities.map(
            (facility: any) =>
              firstValueFrom(this.facilityClient.unassignFacility(facility.id))
          );

          const facilityReAssignments: Promise<any>[] = flatten(
            Object.keys(this.reassignmentMap)?.map((key) =>
              this.reassignmentMap[key]?.map((facility: any) =>
                firstValueFrom(
                  this.facilityClient.moveFacilityToLevel(
                    [facility.id],
                    +key,
                    this.corpId
                  )
                )
              )
            )
          );
          await Promise.all([
            ...facilityReAssignments,
            ...facilityUnAssignments,
          ]);

          await firstValueFrom(
            this.levelClient.deleteLevel(this.currentLevel.id)
          );
        }
        this.dialogRef.close(true);
      } catch (err) {}
    }
  }

  async onFacilityReassignmentChange(levelId: number, facilityIndex: number) {
    if (!this.reassignmentMap[levelId]?.length) {
      this.reassignmentMap[levelId] = [];
    }

    const facility = this.data.facilitiesToReAssign[facilityIndex];

    this.reassignmentMap[levelId]?.push(facility);

    Object.keys(this.reassignmentMap)
      .filter((x) => +x !== +levelId)
      .forEach((key) => {
        if (this.reassignmentMap[key].includes(facility)) {
          this.reassignmentMap[key].splice(
            this.reassignmentMap[key].indexOf(facility),
            1
          );
        }
      });
  }
}
